import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Button, Grid, IconButton, Tooltip, Typography } from '@mui/material';
import { RootState, useAppDispatch, useAppSelector } from '../../store/store';
import { GetTrainingDatasetDetails, handleToggleFileDetailsPopup } from '../../store/mlcreator-slice/mlcreator';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { useEffect, useMemo } from 'react';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Transition from '../common/Transition';
import { Header } from '../common/Header';
import TableRowsIcon from '@mui/icons-material/TableRows';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import CloudDownloadTwoToneIcon from '@mui/icons-material/CloudDownloadTwoTone';
import { DownloadFile } from '../../services/FileServices';
import { EndPoints } from '../../axios/endPoints';
import { BarChart } from '@mui/x-charts/BarChart';
import DescriptionIcon from '@mui/icons-material/Description';
import CategoryIcon from '@mui/icons-material/Category';
import TrendingUpIcon from '@mui/icons-material/TrendingUp'; 
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import BarChartIcon from '@mui/icons-material/BarChart';
import NumbersIcon from '@mui/icons-material/Numbers';
import TagIcon from '@mui/icons-material/Tag';
import { Chip } from '@mui/material';

const CreatorFileDetails = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { fileDetailsPopupOpen, fileDetailsBodyParams, fileDetailsLoading, fileDetails } = useAppSelector((state: RootState) => state.mlcreator);
  
    const columns: GridColDef[] = useMemo(
        () => [
            {
                field: 'id',
                headerName: t('FILE_DETAILS_ID_FIELD'),
                flex: 0.6,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => (
                    <>
                        <TagIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_ID_FIELD')}
                    </>
                ),
                renderCell: (params) => (
                    <Chip
                        label={params.value}
                        size="small"
                        variant="outlined"
                        sx={{ fontWeight: 'bold', color: '#1976d2', borderColor: '#1976d2' }}
                    />
                ),
            },
            {
                field: 'name',
                headerName: t('FILE_DETAILS_COLUMN_NAME_FIELD'),
                flex: 0.9,
                renderHeader: () => (
                    <>
                        <DescriptionIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_COLUMN_NAME_FIELD')}
                    </>
                ),
                renderCell: (params) => (
                    <span style={{ fontWeight: 500, color: '#333' }}>{params.value}</span>
                ),
            },
            {
                field: 'type',
                headerName: t('FILE_DETAILS_COLUMN_TYPE_FIELD'),
                flex: 0.9,
                renderHeader: () => (
                    <>
                        <CategoryIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_COLUMN_TYPE_FIELD')}
                    </>
                ),
                renderCell: (params) => {
                    const type = params.value;
                    const typeStyles: { [key: string]: { color: string; backgroundColor: string } } = {
                        object: { color: '#fff', backgroundColor: '#1976d2' },
                        int64: { color: '#fff', backgroundColor: '#388e3c' },
                        float64: { color: '#fff', backgroundColor: '#f57c00' },
                        default: { color: '#fff', backgroundColor: '#757575' },
                    };
                    const { color, backgroundColor } = typeStyles[type] || typeStyles.default;
                    return (
                        <Chip
                            label={t(`${params.value}`)}
                            size="small"
                            sx={{ color, backgroundColor, fontWeight: 'medium' }}
                        />
                    );
                },
            },
            {
                field: 'min',
                headerName: t('FILE_DETAILS_COLUMN_MIN_FIELD'),
                flex: 0.6,
                renderHeader: () => (
                    <>
                        <TrendingDownIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_COLUMN_MIN_FIELD')}
                    </>
                ),
                renderCell: (params) => (
                    <span style={{ color: params.value < 0 ? '#d32f2f' : '#388e3c' }}>
                        {Number(params.value).toFixed(2)}
                    </span>
                ),
            },
            {
                field: 'max',
                headerName: t('FILE_DETAILS_COLUMN_MAX_FIELD'),
                flex: 0.6,
                renderHeader: () => (
                    <>
                        <TrendingUpIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_COLUMN_MAX_FIELD')}
                    </>
                ),
                renderCell: (params) => (
                    <span style={{ color: params.value < 0 ? '#d32f2f' : '#388e3c' }}>
                        {Number(params.value).toFixed(2)}
                    </span>
                ),
            },
            {
                field: 'count',
                headerName: t('FILE_DETAILS_COLUMN_COUNT_FIELD'),
                headerAlign: 'center',
                align: 'center',
                flex: 0.6,
                renderHeader: () => (
                    <>
                        <NumbersIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_COLUMN_COUNT_FIELD')}
                    </>
                ),
                renderCell: (params) => (
                    <span style={{ fontWeight: 500}}>{params.value.toLocaleString()}</span>
                ),
            },
            {
                field: 'histogram',
                flex: 1.5,
                headerAlign: 'center',
                align: 'center',
                renderHeader: () => (
                    <>
                        <BarChartIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                        {t('FILE_DETAILS_COLUMN_HISTOGRAM_FIELD')}
                    </>
                ),
                renderCell: (params) => {
                    const xLabels = Object.keys(params.value ?? {});
                    const yValues: number[] = Object.values(params.value ?? {});
                    const min: number = 0;
                    const max: number = yValues?.reduce((acc, curr) => curr > acc ? curr : acc, 0);
                    return (
                    params.value != null &&
                    Object.keys(params.value).length > 0 && (
                        <BarChart
                        xAxis={[{ data: xLabels, scaleType: 'band' }]}
                        yAxis={[{ min: min, max: max }]}
                        series={[{ data: yValues }]}
                        leftAxis={null}
                        bottomAxis={null}
                        width={600}
                        height={200}
                        />
                    )
                    );
                },
            },
        ],
    [t]
    );

    function handleClose() {
        dispatch(handleToggleFileDetailsPopup({ open: false, body: null }));
    }
    
    useEffect(() => {
        if(fileDetailsBodyParams) {
            dispatch(GetTrainingDatasetDetails(fileDetailsBodyParams.fileId));
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fileDetailsBodyParams])
    
    if(!fileDetailsBodyParams) return <></>;

    return(
        <Dialog
            fullWidth
            maxWidth="lg"
            open={fileDetailsPopupOpen}
            TransitionComponent={Transition}
            keepMounted
            onClose={(_, reason) => {
                if(reason !== 'backdropClick') handleClose();
            }}
            aria-describedby="alert-dialog-slide-description"
        >
            <Header 
                title={t('FILE_DETAILS_TITLE', { fileName: fileDetailsBodyParams.fileName })} 
            />
            <DialogContent>
                { !fileDetails || fileDetailsLoading ? 
                <Grid container p={2} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    { fileDetailsLoading ? 
                        <Box sx={{ width: '100%' }}>
                            <LinearProgress />
                        </Box>
                    : !fileDetails ? 
                        <Typography variant={'h6'}>{t('NO_FILE_DETAILS')}</Typography>
                    : '' }
                </Grid> : 
                <Grid container sx={{ display: 'flex', flexDirection: 'column' }} spacing={1}>
                    <Grid ml={0.5} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                        
                        <Grid item sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <TableRowsIcon style={{ fontSize: '18px', color: 'darkgray' }}/>
                            <Typography ml={1} variant='body1' style={{fontWeight: 'bold'}}>{t('FILE_DETAILS_ROW_COUNT', { rowCount: fileDetails.rowCount })}</Typography>
                        </Grid>
                        <Grid item sx={{ display: 'flex' }}>
                            <Tooltip title={t('CREATOR_DOWNLOAD_ACTION')}>
                                <IconButton sx={{padding: 0}} onClick={(e) => {
                                    e.stopPropagation();
                                    DownloadFile(EndPoints.ML_CREATOR_DOWNLOAD_CLEANED_TRAINING_DATASET + `?id=${fileDetailsBodyParams.fileId}`)
                                }}>
                                    <CloudDownloadTwoToneIcon className="download-icon" />
                                </IconButton>
                            </Tooltip>
                        </Grid>
                    </Grid>
                    <Grid ml={0.5} item style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                        <ViewWeekIcon style={{ fontSize: '18px', color: 'darkgray' }}/>
                        <Typography ml={1} variant='body1' style={{fontWeight: 'bold'}}>{t('FILE_DETAILS_COLUMN_COUNT', { columnCount: fileDetails.columnCount })}</Typography>
                    </Grid>
                    <Grid item>
                        <Box sx={{ height: 350, width: '100%' }}>
                            <DataGrid
                                rows={fileDetails.columns}
                                columns={columns}
                                initialState={{
                                    pagination: {
                                        paginationModel: {
                                            pageSize: 10,
                                        },
                                    },
                                }}
                                pageSizeOptions={[5, 10, 25]}
                                rowHeight={80}
                                columnHeaderHeight={35}
                            />
                        </Box>
                    </Grid>
                </Grid>
                }
            </DialogContent>
            <DialogActions>
                <Button className="button-text-color" onClick={handleClose}>{t('CLOSE_BUTTON')}</Button>
            </DialogActions> 
        </Dialog>
    )
}
  
export default CreatorFileDetails;